
    import { computed, defineComponent, onBeforeMount } from 'vue'
    import { useI18n } from 'vue-i18n-composable'
    import { useRouter } from 'vue-router/composables'

    import PostPreviewList from '@/app/components/common/posts/PostPreviewList.vue'
    import { useTagsStore } from '@/app/stores/tags'
    import { useBreadcrumbsStore } from '@/app/stores/breadcrumbs'
    import { usePostsStore } from '@/app/stores/posts'

    import { useHeadful } from '@/common/composables/libs/headful'
    import NotFoundError from '@/common/errors/not-found-error'

    export default defineComponent({
        components: {
            PostPreviewList
        },
        props: {
            slug: {
                type: String,
                required: true
            },
            page: {
                type: Number,
                default: 1
            }
        },
        setup (props) {
            const headful = useHeadful()
            const i18n = useI18n()
            const router = useRouter()
            const postsStore = usePostsStore()
            const tagsStore = useTagsStore()

            const data = computed(() => postsStore.byTag(props.slug))
            const entries = computed(() => data.value.data)
            const paginationMeta = computed(() => data.value.meta)

            const paginate = async (page: number): Promise<void> => {
                await router.push({
                    name: 'posts.by-tag',
                    params: {
                        page: page.toString(),
                        slug: props.slug
                    }
                })

                await postsStore.loadByTag({
                    tagSlug: props.slug,
                    page: page
                })
            }

            onBeforeMount(async (): Promise<void> => {
                if (props.slug === '') {
                    throw new NotFoundError()
                }

                // Ensure that tags module is loaded, then load tag data
                // Loading store here is needed because if we enter the url directly, there can be race-condition
                // between tags module and this and tag becomes undefined. Therefore, we force tag store loading here
                await tagsStore.loadBySlug(props.slug)

                const tag = tagsStore.bySlug(props.slug)

                if (tag === undefined) {
                    throw new NotFoundError()
                }

                // We loaded tags store, so now we are safe that tag won't be undefined, and we can safely set breadcrumbs
                // and metadata
                useBreadcrumbsStore().set([
                    { text: i18n.t('nav.breadcrumbs.posts').toString() },
                    { text: i18n.t('nav.breadcrumbs.tags').toString() },
                    { text: tag.name }
                ])

                headful({
                    title: `${tag.name} | ${i18n.t('meta.title')}`
                })

                await postsStore.loadByTag({
                    tagSlug: props.slug,
                    page: props.page
                })
            })

            return {
                entries,
                paginationMeta,
                paginate
            }
        }
    })
